import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Activity, Archive, Pause, UserPlus, Users } from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Avatar } from '../../components/Avatar'
import { api, extractApiError } from '../../lib/api'
import { platformUsersResponse, platformTenantsResponse } from '../../lib/types'
import { PlatformUserRowActions } from '../../components/PlatformUserRowActions'
import { formatDateTime, formatNumber } from '../../lib/format'
import { StatusPill, RolePill } from '../../components/ui/Pills'
import { DataTable } from '../../components/table/DataTable'
import { usePageSize } from '../../lib/display-prefs'

/* ============================ helpers ============================ */

/** Platform role → shared RolePill label + tone. The All Users table
 *  only ever shows the three platform roles: User reads violet, the two
 *  admin tiers read amber. */
function roleBadge(role) {
  if (role === 'super_admin') return { label: 'Master Admin', tone: 'amber' }
  if (role === 'company_admin') return { label: 'Company Admin', tone: 'amber' }
  if (role === 'sub_admin') return { label: 'Sub Admin', tone: 'amber' }
  return { label: 'User', tone: 'violet' }
}

const STATUS_LABEL = {
  active: 'Active',
  invited: 'Invited',
  suspended: 'Suspended',
  offboarded: 'Offboarded',
}

/** Effective status text — mirrors StatusPill: a user whose company is
 *  blocked reads as the COMPANY's state. Used for the exported value. */
function statusLabel(u) {
  const blocked = Boolean(u.tenantStatus) && u.tenantStatus !== 'active'
  const eff = blocked ? u.tenantStatus : u.status
  return STATUS_LABEL[eff] ?? eff
}

const Dash = () => <span className="text-[color:var(--color-ink-subtle)]">—</span>

function NameCell({ user }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <Avatar name={user.fullName} avatarUrl={user.avatarUrl ?? null} size="md" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[color:var(--color-ink-strong)] truncate m-0">
          {user.fullName}
        </p>
      </div>
    </div>
  )
}

/* ============================ page ============================ */

export function AllUsersPage() {
  const [search, setSearch] = useState('')
  // Single-value server filters, surfaced through the DataTable filter
  // drawer as radio / select groups (the /platform/users endpoint takes
  // one value per facet).
  const [filters, setFilters] = useState({ role: '', status: '', company: '' })
  const [page, setPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [limit, setLimit] = usePageSize()

  // Company catalogue for the Company filter select.
  const companiesQuery = useQuery({
    queryKey: ['all-users-company-filter'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get('/super-admin/tenants', { params: { limit: 100 } })
      return platformTenantsResponse.parse(res.data)
    },
  })
  const companyOptions = useMemo(
    () =>
      [...(companiesQuery.data?.data ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({ value: t.id, label: t.name })),
    [companiesQuery.data],
  )

  /** Shared query params for the current search + filters (no paging). */
  const baseParams = useCallback(() => {
    const p = {}
    if (search.trim()) p.search = search.trim()
    if (filters.role) p.role = filters.role
    if (filters.status) p.status = filters.status
    if (filters.company) p.tenantId = filters.company
    return p
  }, [search, filters])

  const usersQuery = useQuery({
    queryKey: ['platform-users', search, filters, page, limit],
    queryFn: async () => {
      const res = await api.get('/super-admin/platform/users', {
        params: { ...baseParams(), page, limit },
      })
      return platformUsersResponse.parse(res.data)
    },
    placeholderData: (prev) => prev,
  })

  const rows = useMemo(() => usersQuery.data?.data ?? [], [usersQuery.data])
  const pageInfo = usersQuery.data?.pagination ?? null
  const summary = usersQuery.data?.summary ?? null

  const hasActiveFilter =
    search.trim() !== '' || filters.role || filters.status || filters.company

  const clearFilters = useCallback(() => {
    setSearch('')
    setFilters({ role: '', status: '', company: '' })
    setPage(1)
  }, [])

  function setStatus(value) {
    setFilters((f) => ({ ...f, status: f.status === value ? '' : value }))
    setPage(1)
  }

  /** Fetch every user matching the current filters — for the export
   *  "All records" / "Selected records" scopes and select-all. Pages
   *  through at the 500-row cap. */
  const fetchAll = useCallback(async () => {
    const acc = []
    let p = 1
    for (;;) {
      const res = await api.get('/super-admin/platform/users', {
        params: { ...baseParams(), page: p, limit: 500 },
      })
      const parsed = platformUsersResponse.parse(res.data)
      acc.push(...parsed.data)
      const tp = parsed.pagination?.totalPages ?? 1
      if (p >= tp || p >= 50) break
      p += 1
    }
    return acc
  }, [baseParams])

  const getExportRows = useCallback(
    async (scope, ids) => {
      const all = await fetchAll()
      if (scope === 'selected' && ids) {
        const set = new Set(ids)
        return all.filter((u) => set.has(u.id))
      }
      return all
    },
    [fetchAll],
  )

  const columns = useMemo(
    () => [
      {
        key: 'fullName',
        header: 'Full Name',
        minWidth: 220,
        grow: true,
        pinnable: true,
        sortAccessor: (u) => (u.fullName ?? '').toLowerCase(),
        searchAccessor: (u) => `${u.fullName ?? ''} ${u.emailId ?? ''}`,
        exportValue: (u) => u.fullName,
        render: (u) => <NameCell user={u} />,
      },
      {
        key: 'emailId',
        header: 'Email ID',
        minWidth: 220,
        sortAccessor: (u) => (u.emailId ?? '').toLowerCase(),
        exportValue: (u) => u.emailId,
        render: (u) => (
          <span className="text-[color:var(--color-ink-muted)] truncate block">
            {u.emailId}
          </span>
        ),
      },
      {
        key: 'tenantName',
        header: 'Company',
        minWidth: 140,
        sortAccessor: (u) => (u.tenantName ?? '').toLowerCase(),
        exportValue: (u) => (u.tenantId ? u.tenantName : 'Platform'),
        render: (u) =>
          u.tenantId ? (
            <span className="text-[color:var(--color-ink)] truncate block">
              {u.tenantName}
            </span>
          ) : (
            <span className="text-[color:var(--color-ink-muted)] truncate block italic">
              Platform
            </span>
          ),
      },
      {
        key: 'role',
        header: 'Role',
        minWidth: 150,
        cellClassName: 'whitespace-nowrap',
        sortAccessor: (u) => u.role ?? '',
        exportValue: (u) => roleBadge(u.role).label,
        render: (u) => <RolePill {...roleBadge(u.role)} />,
      },
      {
        key: 'workRole',
        header: 'Work role',
        minWidth: 160,
        sortAccessor: (u) => u.workRoleName ?? u.workRole ?? '',
        exportValue: (u) =>
          u.role === 'user' ? u.workRoleName ?? 'Employee' : '',
        render: (u) =>
          u.role === 'user' ? (
            <span className="text-[color:var(--color-ink)]">
              {u.workRoleName ?? 'Employee'}
            </span>
          ) : (
            <Dash />
          ),
      },
      {
        key: 'department',
        header: 'Department',
        minWidth: 190,
        sortAccessor: (u) => (u.departments?.[0] ?? '').toLowerCase(),
        exportValue: (u) => (u.departments ?? []).join(', '),
        render: (u) =>
          u.departments && u.departments.length > 0 ? (
            <span className="text-[color:var(--color-ink)]">
              {u.departments.join(', ')}
            </span>
          ) : (
            <Dash />
          ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 100,
        sortAccessor: (u) => u.status ?? '',
        exportValue: (u) => statusLabel(u),
        render: (u) => (
          <StatusPill status={u.status} companyStatus={u.tenantStatus} />
        ),
      },
      {
        key: 'createdAt',
        header: 'Joined',
        minWidth: 130,
        sortAccessor: (u) => (u.createdAt ? new Date(u.createdAt) : null),
        exportValue: (u) => (u.createdAt ? formatDateTime(u.createdAt) : ''),
        render: (u) => (
          <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums whitespace-nowrap">
            {formatDateTime(u.createdAt)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        minWidth: 72,
        align: 'center',
        pinnedRight: true,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (u) => <PlatformUserRowActions user={u} />,
      },
    ],
    [],
  )

  const filterConfig = useMemo(
    () => [
      {
        key: 'role',
        label: 'Role',
        type: 'radio',
        anyLabel: 'All roles',
        options: [
          { value: 'company_admin', label: 'Company Admin' },
          { value: 'sub_admin', label: 'Sub Admin' },
          { value: 'user', label: 'User' },
        ],
      },
      {
        key: 'status',
        label: 'Status',
        type: 'radio',
        anyLabel: 'All statuses',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'invited', label: 'Invited' },
          { value: 'suspended', label: 'Suspended' },
          { value: 'offboarded', label: 'Offboarded' },
        ],
      },
      {
        key: 'company',
        label: 'Company',
        type: 'select',
        anyLabel: 'All companies',
        options: companyOptions,
      },
    ],
    [companyOptions],
  )

  return (
    <div>
      <Breadcrumb current="All Users" homePath="/super-admin/dashboard" />

      <div className="mb-5 flex items-start gap-3">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)] shrink-0">
          <Users size={22} />
        </span>
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] leading-tight">
            All Users
          </h1>
          <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
            Every user across all companies.
          </p>
        </div>
      </div>

      {/* KPI strip — Users (Total) is platform-wide; the rest are
       *  per-status counts. Each tile toggles the matching status
       *  filter (the Total tile clears every filter). */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-5">
        <KpiCard
          label="Users (Total)"
          value={summary?.platformTotal ?? pageInfo?.total ?? rows.length}
          icon={Users}
          tone="violet"
          loading={usersQuery.isLoading}
          onClick={clearFilters}
          selected={!hasActiveFilter}
        />
        <KpiCard
          label="Active"
          value={summary?.statusCounts?.active ?? 0}
          icon={Activity}
          tone="emerald"
          loading={usersQuery.isLoading}
          onClick={() => setStatus('active')}
          selected={filters.status === 'active'}
        />
        <KpiCard
          label="Invited"
          value={summary?.statusCounts?.invited ?? 0}
          icon={UserPlus}
          tone="sky"
          loading={usersQuery.isLoading}
          onClick={() => setStatus('invited')}
          selected={filters.status === 'invited'}
        />
        <KpiCard
          label="Suspended"
          value={summary?.statusCounts?.suspended ?? 0}
          icon={Pause}
          tone="amber"
          loading={usersQuery.isLoading}
          onClick={() => setStatus('suspended')}
          selected={filters.status === 'suspended'}
        />
        <KpiCard
          label="Offboarded"
          value={summary?.statusCounts?.offboarded ?? 0}
          icon={Archive}
          tone="rose"
          loading={usersQuery.isLoading}
          onClick={() => setStatus('offboarded')}
          selected={filters.status === 'offboarded'}
        />
      </div>

      <DataTable
        tableId="platform-all-users"
        columns={columns}
        rows={rows}
        getRowId={(u) => u.id}
        loading={usersQuery.isLoading}
        fetching={usersQuery.isFetching}
        error={
          usersQuery.isError
            ? extractApiError(usersQuery.error).message
            : null
        }
        onRetry={() => usersQuery.refetch()}
        title="All Users"
        exportBaseName="all-users"
        exportTitle="All Users"
        getExportRows={getExportRows}
        selectable
        onSelectAllRecords={async () => (await fetchAll()).map((u) => u.id)}
        search={search}
        onSearchChange={(v) => {
          setSearch(v)
          setPage(1)
        }}
        searchPlaceholder="Search by Full Name or Email ID"
        filterConfig={filterConfig}
        filterValues={filters}
        onFilterChange={(v) => {
          setFilters(v)
          setPage(1)
        }}
        defaultSort={[{ key: 'createdAt', dir: 'desc' }]}
        page={pageInfo?.page ?? page}
        totalPages={pageInfo?.totalPages ?? 1}
        total={pageInfo?.total ?? 0}
        limit={pageInfo?.limit ?? limit}
        onPageChange={setPage}
        onLimitChange={(n) => {
          setLimit(n)
          setPage(1)
        }}
        emptyState={{
          icon: Users,
          title: hasActiveFilter
            ? 'No users match your filters'
            : 'No users yet',
          body: hasActiveFilter
            ? 'Try a different search term, role, status or company. Or clear the filters to see everyone on the platform.'
            : 'When companies invite their first members, they will show up here.',
        }}
      />
    </div>
  )
}

/* ============================ KPI tile ============================ */

const KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  amber: {
    tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
  },
  sky: {
    tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
}

function KpiCard({ label, value, icon: Icon, tone = 'violet', loading, onClick, selected }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!!selected}
      className={
        'group flex flex-col relative overflow-hidden rounded-xl border bg-[color:var(--color-card)] p-5 min-h-[120px] text-left w-full transition-colors cursor-pointer ' +
        (selected
          ? 'border-[color:var(--color-accent)] ring-1 ring-[color:var(--color-accent)] '
          : 'border-[color:var(--color-rule)] hover:border-[color:var(--color-accent)] ')
      }
    >
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">
            {label}
          </h3>
          {loading ? (
            <div className="mt-3 h-7 w-14 rounded animate-pulse bg-[color:var(--color-rule)]" />
          ) : (
            <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
              {formatNumber(Number(value ?? 0))}
            </p>
          )}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </button>
  )
}
